import React, { useEffect, useRef, useState, Suspense, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";

import Spinner from "../../Components/Spinner";
import { vehicleService } from "../../../Services/vehicle.service";
import { vehicleGroupService } from "../../../Services/vehicle.group.service";
import { visitsService } from "../../../Services/visits.service";

import CryptoJS from "crypto-js";
import { t } from "i18next";
import { ToastComponent } from "../../Components/ToastComponent";
import ExpandToggle from "../../../components/ExpandToggle";

const GoogleMapComponent = React.lazy(() =>
  import("../../Components/Maps/GoogleMapComponent")
);

const vehicleIdOf = (v) => Number(v?.vehicleId ?? v?.id ?? v?.groupVehicleId) || 0;
const vehicleLabelOf = (v) => v?.alias ?? v?.plateAlias ?? v?.plate ?? v?.licensePlate ?? "";

export default function ViewMap() {
  const { groupfilter, vehiclefilter, showVisits } = useParams();
  const { isDark } = useTheme();

  const extractData = useCallback(() => {
    const secretKey = process.env.REACT_APP_CRYPTO_SECRET;
    let group = 0;
    let vehicle = 0;
    let showVisit = false;

    const decryptValue = (val) => {
      if (!val) return "";
      try {
        let clean = decodeURIComponent(val.toString());
        clean = clean.replace(/ /g, "+"); // resolve problemas de decodificação de Base64
        const bytes = CryptoJS.AES.decrypt(clean, secretKey);
        return bytes.toString(CryptoJS.enc.Utf8);
      } catch (e) {
        console.error("Erro ao decriptar valor:", val, e);
        return "";
      }
    };

    if (groupfilter) {
      const dec = decryptValue(groupfilter);
      group = Number(dec) || 0;
    }

    if (vehiclefilter) {
      const dec = decryptValue(vehiclefilter);
      vehicle = Number(dec) || 0;
    }

    if (showVisits) {
      const dec = decryptValue(showVisits);
      showVisit = dec === "true";
    }

    return { group, vehicle, showVisit };
  }, [groupfilter, vehiclefilter, showVisits]);

  const { group: urlGroupId, vehicle: urlVehicleId } = extractData();
  const openerLock = window.opener?.__radarLock__;
  const initialGroupId = Number(openerLock?.groupId ?? urlGroupId) || 0;
  const initialVehicleId = Number(openerLock?.vehicleId ?? urlVehicleId) || 0;
  const initialGroupName = openerLock?.groupName || "";
  const initialVehicleName = openerLock?.vehicleName || "";

  const isVehicleLocked = initialVehicleId > 0;
  const isGroupLocked = initialVehicleId > 0 || initialGroupId > 0;

  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [visitsLocations, setVisitLocations] = useState([]);
  const [startInterval, setStartInterval] = useState(false);
  const [createMap, setCreateMap] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const [barGroups, setBarGroups] = useState([]);
  const [barVehicles, setBarVehicles] = useState([]);
  const [barSelectedGroup, setBarSelectedGroup] = useState(initialGroupId);
  const [barSelectedVehicle, setBarSelectedVehicle] = useState(initialVehicleId);
  const [barLoadingVehicles, setBarLoadingVehicles] = useState(false);

  const [toastParams, setToastParams] = useState("");
  // Não abre o infoview automaticamente ao abrir a tela: o mapa/imagem ainda
  // não carregou nesse momento e o painel aparece vazio. Só abre quando o
  // usuário clicar no veículo no mapa.
  const [openInfoWindowVehicleId, setOpenInfoWindowVehicleId] = useState(0);

  const intervalIdRef = useRef(null);
  const lastReceivedRef = useRef(null);
  const initialFetchRef = useRef(true);
  const delay = Number(process.env.REACT_APP_UPDATE_FLEET_DATA_TIMER) || 5000;

  const updateSmartVehiclesList = (prevList, newList) => {
    let changed = false;
    const map = new Map(prevList.map((v) => [v.id, v]));
    const result = newList.map((newItem) => {
      const oldItem = map.get(newItem.id);
      if (!oldItem || oldItem.generatedAt !== newItem.generatedAt) { changed = true; return newItem; }
      return oldItem;
    });
    return changed ? result : prevList;
  };

  const fetchTrackingList = async (groupId, vehicleId) => {
    try {
      const trackingList = await vehicleService.fetchTrackVehiclesAll(Number(groupId), Number(vehicleId));
      if (trackingList?.vehiclesUser && Array.isArray(trackingList.vehiclesUser)) {
        const currentLastReceived = lastReceivedRef.current ?? 0;
        const newLastReceived = new Date(trackingList.lastReceived).getTime();
        if (!currentLastReceived) {
          setLocations(trackingList);
          lastReceivedRef.current = newLastReceived;
          return;
        }
        if (newLastReceived > currentLastReceived) {
          lastReceivedRef.current = newLastReceived;
          setLocations((prev) => {
            const prevVehicles = Array.isArray(prev?.vehiclesUser) ? prev.vehiclesUser : [];
            const updatedVehicles = updateSmartVehiclesList(prevVehicles, trackingList.vehiclesUser);
            return { ...prev, ...trackingList, vehiclesUser: updatedVehicles };
          });
        }
      }
      return trackingList;
    } catch (error) {
      if (error?.response?.status === 401 || error?.message?.includes("401")) setSessionExpired(true);
    }
  };

  const fetchVisits = async () => {
    try {
      const visits = await visitsService.fetchVisits(1, 0);
      if (visits) setVisitLocations(visits);
    } catch { }
  };

  const fetchData = async () => {
    const { group, vehicle, showVisit } = extractData();
    try {
      if (initialFetchRef.current) {
        setLoading(true);
        if (showVisit) await fetchVisits();
      }
      await fetchTrackingList(group, vehicle);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      await fetchData();
      initialFetchRef.current = false;
      setStartInterval(true);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialGroupId > 0) {

      setBarSelectedGroup(initialGroupId);
    }
  }, [initialGroupId]);

  useEffect(() => {
    if (initialVehicleId > 0) {
      setBarSelectedVehicle(initialVehicleId);
    }
  }, [initialVehicleId]);

  useEffect(() => {
    if (!startInterval) return;
    setCreateMap(false);
    intervalIdRef.current = setInterval(() => { fetchData(); }, delay);
    return () => { if (intervalIdRef.current) { clearInterval(intervalIdRef.current); intervalIdRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startInterval]);

  useEffect(() => {
    (async () => {
      try {
        const groups = await vehicleGroupService.fetchGroups();
        setBarGroups(Array.isArray(groups) ? groups : []);
      } catch {
        setBarGroups([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setBarLoadingVehicles(true);
      try {
        const groupId = isGroupLocked ? initialGroupId : barSelectedGroup;
        const list = await vehicleService.fetchVehicles(groupId);
        setBarVehicles(Array.isArray(list) ? list : []);
      } catch {
        setBarVehicles([]);
      } finally {
        setBarLoadingVehicles(false);
      }
    })();
  }, [barSelectedGroup, isGroupLocked, initialGroupId]);



  console.log("[ViewMap Debug]", {
    urlGroupId,
    urlVehicleId,
    openerLock,
    initialGroupId,
    initialVehicleId,
    barSelectedGroup,
    barSelectedVehicle,
    barVehiclesCount: barVehicles.length,
    barVehicles: barVehicles.map(v => ({ id: vehicleIdOf(v), label: vehicleLabelOf(v) }))
  });

  if (sessionExpired) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", backgroundColor: "#1a1a2e", color: "#fff", gap: 24 }}>
        <h2 style={{ fontSize: 32 }}>Sessão expirada</h2>
        <p style={{ fontSize: 18, color: "#aaa" }}>A sessão foi encerrada. Recarregue a página para continuar.</p>
        <button style={{ padding: "12px 32px", fontSize: 18, borderRadius: 8, border: "none", backgroundColor: "#e67e22", color: "#fff", cursor: "pointer" }} onClick={() => window.location.reload()}>
          Recarregar
        </button>
      </div>
    );
  }

  const TOOLBAR_HEIGHT = 54;
  const toolbarStyle = {
    position: "fixed",
    top: 0, left: 0, right: 0,
    zIndex: 1000,
    backgroundColor: "var(--bg-navbar)",
    color: "var(--text-primary)",
    padding: "6px 12px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  };

  const selectStyle = (locked) => ({
    height: 32,
    fontSize: 13,
    padding: "0 8px",
    borderRadius: 4,
    border: isDark ? "1px solid var(--border-secondary, #444a5e)" : "1px solid var(--border-color, #ced4da)",
    backgroundColor: isDark ? "var(--bg-primary, #2b2c40)" : "#fff",
    color: isDark ? "var(--text-primary, #fff)" : "#212529",
    minWidth: 160,
    opacity: locked ? 0.55 : 1,
    pointerEvents: locked ? "none" : "auto",
  });

  const optionStyle = {
    backgroundColor: isDark ? "var(--bg-primary, #2b2c40)" : "#fff",
    color: isDark ? "var(--text-primary, #fff)" : "#212529",
  };

  const labelStyle = { fontSize: 12, display: "flex", flexDirection: "column", gap: 2 };

  return (
    <>
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <div style={toolbarStyle}>
        <label style={labelStyle}>
          <span>{t("VIEW_MAP_FILTERS.GROUPS")}</span>
          <select
            style={selectStyle(isGroupLocked)}
            value={barSelectedGroup}
            disabled={isGroupLocked}
            onChange={(e) => {
              const gId = Number(e.target.value) || 0;
              setBarSelectedGroup(gId);
              setBarSelectedVehicle(0);
              setOpenInfoWindowVehicleId(0);
            }}
          >
            <option value={0} style={optionStyle}>{t("VIEW_MAP_FILTERS.ALL_GROUPS")}</option>
            {barGroups.map((g) => (
              <option key={g.vehicleGroupId} value={g.vehicleGroupId} style={optionStyle}>
                {g.vehicleGroupName ?? g.description ?? g.name}
              </option>
            ))}
            {barSelectedGroup > 0 && !barGroups.some((g) => Number(g.vehicleGroupId) === barSelectedGroup) && (
              <option value={barSelectedGroup} style={optionStyle}>
                {initialGroupName || `Grupo #${barSelectedGroup}`}
              </option>
            )}
          </select>
        </label>

        <label style={labelStyle}>
          <span>{t("VIEW_MAP_FILTERS.VEHICLES")}</span>
          <select
            style={selectStyle(isVehicleLocked)}
            value={barSelectedVehicle}
            disabled={isVehicleLocked || barLoadingVehicles}
            onChange={(e) => {
              const vId = Number(e.target.value) || 0;
              setBarSelectedVehicle(vId);
              setOpenInfoWindowVehicleId(vId);
              if (vId > 0) {
                const vehiclesOnMap = locations?.vehiclesUser || [];
                const found = vehiclesOnMap.some((v) => Number(v.vehicleId || v.id) === vId);
                if (!found) {
                  setToastParams(
                    JSON.stringify({
                      show: true,
                      title: "Veículo não encontrado",
                      body: "O veículo selecionado não possui posição disponível no mapa.",
                      color: "warning",
                    })
                  );
                }
              }
            }}
          >
            <option value={0} style={optionStyle}>
              {barLoadingVehicles ? `${t("COMMON.LOADING")}...` : t("VIEW_MAP_FILTERS.ALL_VEHICLES")}
            </option>
            {barVehicles.map((v) => {
              const id = vehicleIdOf(v);
              if (!id) return null;
              return (
                <option key={id} value={id} style={optionStyle}>
                  {vehicleLabelOf(v)}
                </option>
              );
            })}
            {barSelectedVehicle > 0 && !barVehicles.some((v) => vehicleIdOf(v) === barSelectedVehicle) && (
              <option value={barSelectedVehicle} style={optionStyle}>
                {initialVehicleName || vehicleLabelOf((locations?.vehiclesUser || []).find((v) => Number(v?.vehicleId ?? v?.id) === barSelectedVehicle)) || "Veículo Selecionado"}
              </option>
            )}
          </select>
        </label>

        <div style={{ flex: 1 }} />
        <ExpandToggle size="md" />
      </div>

      <div style={{ paddingTop: TOOLBAR_HEIGHT, height: "100vh", boxSizing: "border-box" }}>
        <Suspense fallback={<Spinner />}>
          <GoogleMapComponent
            mapLoading={loading}
            locations={locations}
            activeTab={"map"}
            createMap={createMap}
            hideIcons={true}
            visitsLocations={visitsLocations}
            handleClick={false}
            openInfoWindowVehicleId={openInfoWindowVehicleId}
          />
        </Suspense>
      </div>
    </>
  );
}
